import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useCampusPicker } from '@/hooks/usePickers';
import { assetCategoriesApi, assetLocationsApi } from '@/services/api/endpoints/assets';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import {
  LOCATION_KINDS,
  type AssetCategory,
  type AssetCategoryInput,
  type AssetLocation,
  type AssetLocationInput,
  type AssetRecordStatus,
  type LocationKind,
} from '@/types/asset';
import { Badge, LOCATION_KIND_LABEL, RecordStatusBadge } from './shared';

/**
 * The register's reference data: categories and locations.
 *
 * <p>Both are retired, never deleted — assets and stock items point at them
 * for as long as the school keeps records. Usage and asset counts are the
 * server's. Whether a category is consumable is fixed once it is used, and a
 * location cannot be retired while assets are recorded there; the server
 * refuses both and its reason is shown as it gave it.
 */
export function AssetSetupPage() {
  const { can } = useAuth();
  const manages = can(PERMISSIONS.ASSET_MANAGE);

  return (
    <div>
      <PageHeader
        title="Asset setup"
        description="Categories and locations the register is filed under. Retired, never deleted."
        icon={<Settings2 className="h-5 w-5" aria-hidden="true" />}
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <CategoriesPanel manages={manages} />
        <LocationsPanel manages={manages} />
      </div>
    </div>
  );
}

/** A retire or restore waiting for confirmation. */
interface StatusChange<T> {
  row: T;
  status: AssetRecordStatus;
}

function PanelTitle({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
      <div className="min-w-0">
        <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">{title}</h2>
        <p className="mt-0.5 text-xs text-muted">{description}</p>
      </div>
      {action}
    </div>
  );
}

// -------------------------------------------------------------- categories

function CategoriesPanel({ manages }: { manages: boolean }) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AssetCategory | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [consumable, setConsumable] = useState(false);
  const [changing, setChanging] = useState<StatusChange<AssetCategory> | null>(null);

  const categories = useQuery({
    queryKey: queryKeys.assetReference.categories,
    queryFn: () => assetCategoriesApi.list(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.assetReference.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
  };

  function open(category: AssetCategory | null) {
    setEditing(category);
    setCode(category?.code ?? '');
    setName(category?.name ?? '');
    setDescription(category?.description ?? '');
    setConsumable(category?.consumable ?? false);
    setFormOpen(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: AssetCategoryInput = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() === '' ? undefined : description.trim(),
        consumable,
      };
      return editing === null
        ? assetCategoriesApi.create(payload)
        : assetCategoriesApi.update(editing.id, payload);
    },
    onSuccess: (category) => {
      toast.success(`${category.name} saved`);
      setFormOpen(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: (change: StatusChange<AssetCategory>) =>
      assetCategoriesApi.setStatus(change.row.id, change.status),
    onSuccess: (category) => {
      toast.success(`${category.name} ${category.status === 'ACTIVE' ? 'restored' : 'retired'}`);
      setChanging(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  // The server's usage figure decides the lock; the form only reflects it.
  const consumableLocked = editing !== null && editing.usage > 0;

  const columns: Column<AssetCategory>[] = [
    {
      key: 'name',
      header: 'Category',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">{row.name}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      render: (row) =>
        row.consumable ? <Badge tone="info">consumable</Badge> : <Badge>tagged</Badge>,
    },
    {
      key: 'usage',
      header: 'In use',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.usage}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <RecordStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex flex-wrap justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setChanging({ row, status: row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' })
              }
            >
              {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <Card>
      <PanelTitle
        title="Categories"
        description="Consumables are counted as stock; everything else is tagged one by one."
        action={manages ? <Button size="sm" onClick={() => open(null)}>Add a category</Button> : undefined}
      />
      <DataTable
        columns={columns}
        rows={categories.data}
        rowKey={(row) => row.id}
        loading={categories.isPending}
        error={categories.error}
        onRetry={() => void categories.refetch()}
        emptyTitle="No categories yet"
        emptyDescription="Add a category before registering assets or stock."
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing === null ? 'Add a category' : `Edit ${editing.name}`}
        description="Codes are unique per school."
        footer={
          <>
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={code.trim() === '' || name.trim() === ''}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Code"
              value={code}
              maxLength={32}
              onChange={(event) => setCode(event.target.value)}
              required
            />
            <TextField
              label="Name"
              value={name}
              maxLength={150}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </div>
          <TextField
            label="Description"
            value={description}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
          />
          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={consumable}
              disabled={consumableLocked}
              onChange={(event) => setConsumable(event.target.checked)}
              className="mt-0.5 h-4 w-4 border-line"
            />
            <span>
              Consumable
              <span className="block text-[12px] text-muted">
                {consumableLocked
                  ? `Fixed: ${editing?.usage} asset${editing?.usage === 1 ? '' : 's'} or stock item${editing?.usage === 1 ? '' : 's'} already use this category.`
                  : 'Counted as stock rather than tagged individually. Fixed once the category is in use.'}
              </span>
            </span>
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        open={changing !== null}
        title={
          changing === null
            ? ''
            : `${changing.status === 'ACTIVE' ? 'Restore' : 'Retire'} ${changing.row.name}?`
        }
        message={
          changing?.status === 'ACTIVE'
            ? 'It becomes available again for new assets and stock items.'
            : 'It stays on everything already filed under it, but cannot be chosen for anything new.'
        }
        confirmLabel={changing?.status === 'ACTIVE' ? 'Restore' : 'Retire'}
        destructive={changing?.status === 'INACTIVE'}
        loading={setStatus.isPending}
        onConfirm={() => {
          if (changing !== null) {
            setStatus.mutate(changing);
          }
        }}
        onCancel={() => setChanging(null)}
      />
    </Card>
  );
}

// --------------------------------------------------------------- locations

function LocationsPanel({ manages }: { manages: boolean }) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const campuses = useCampusPicker();

  const [campusFilter, setCampusFilter] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AssetLocation | null>(null);
  const [campusId, setCampusId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<LocationKind | ''>('');
  const [description, setDescription] = useState('');
  const [changing, setChanging] = useState<StatusChange<AssetLocation> | null>(null);

  const filterCampus = campusFilter === '' ? undefined : campusFilter;
  const locations = useQuery({
    queryKey: queryKeys.assetReference.locations(filterCampus),
    queryFn: () => assetLocationsApi.list(filterCampus),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.assetReference.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all });
  };

  function open(location: AssetLocation | null) {
    setEditing(location);
    setCampusId(location?.campusId ?? campusFilter);
    setName(location?.name ?? '');
    setKind(location?.kind ?? '');
    setDescription(location?.description ?? '');
    setFormOpen(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: AssetLocationInput = {
        campusId,
        name: name.trim(),
        kind: kind as LocationKind,
        description: description.trim() === '' ? undefined : description.trim(),
      };
      return editing === null
        ? assetLocationsApi.create(payload)
        : assetLocationsApi.update(editing.id, payload);
    },
    onSuccess: (location) => {
      toast.success(`${location.name} saved`);
      setFormOpen(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: (change: StatusChange<AssetLocation>) =>
      assetLocationsApi.setStatus(change.row.id, change.status),
    onSuccess: (location) => {
      toast.success(`${location.name} ${location.status === 'ACTIVE' ? 'restored' : 'retired'}`);
      setChanging(null);
      invalidate();
    },
    // The server refuses to retire a location that still holds assets; its reason is shown verbatim.
    onError: (error) => {
      handleApiError(error);
      setChanging(null);
    },
  });

  const campusOptions = campuses.data ?? [];

  const columns: Column<AssetLocation>[] = [
    {
      key: 'name',
      header: 'Location',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">{row.name}</span>
          <span className="block text-[11px] text-subtle">{row.campusName ?? '—'}</span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      render: (row) => LOCATION_KIND_LABEL[row.kind],
    },
    {
      key: 'assets',
      header: 'Assets',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.assetCount}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <RecordStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex flex-wrap justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setChanging({ row, status: row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' })
              }
            >
              {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <Card>
      <PanelTitle
        title="Locations"
        description="Rooms and stores where assets are kept, per campus."
        action={
          <span className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Campus"
              value={campusFilter}
              onChange={setCampusFilter}
              options={campusOptions.map((row) => ({ value: row.id, label: row.name }))}
            />
            {manages && (
              <Button size="sm" onClick={() => open(null)}>
                Add a location
              </Button>
            )}
          </span>
        }
      />
      <DataTable
        columns={columns}
        rows={locations.data}
        rowKey={(row) => row.id}
        loading={locations.isPending}
        error={locations.error}
        onRetry={() => void locations.refetch()}
        emptyTitle={campusFilter === '' ? 'No locations yet' : 'No locations on this campus'}
        emptyDescription="Add a location so assets have somewhere to be."
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing === null ? 'Add a location' : `Edit ${editing.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={campusId === '' || name.trim() === '' || kind === ''}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <SelectField
            label="Campus"
            value={campusId}
            onChange={(event) => setCampusId(event.target.value)}
            required
            hint={
              campuses.isError
                ? 'Campuses could not be loaded.'
                : !campuses.isPending && campusOptions.length === 0
                  ? 'No campuses are available to you.'
                  : undefined
            }
          >
            <option value="">Choose a campus</option>
            {campusOptions.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </SelectField>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Name"
              value={name}
              maxLength={150}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <SelectField
              label="Kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as LocationKind | '')}
              required
            >
              <option value="">Choose a kind</option>
              {LOCATION_KINDS.map((value) => (
                <option key={value} value={value}>
                  {LOCATION_KIND_LABEL[value]}
                </option>
              ))}
            </SelectField>
          </div>
          <TextField
            label="Description"
            value={description}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={changing !== null}
        title={
          changing === null
            ? ''
            : `${changing.status === 'ACTIVE' ? 'Restore' : 'Retire'} ${changing.row.name}?`
        }
        message={
          changing?.status === 'ACTIVE'
            ? 'Assets can be placed here again.'
            : 'It can no longer receive assets. The server refuses this while assets are still recorded here.'
        }
        confirmLabel={changing?.status === 'ACTIVE' ? 'Restore' : 'Retire'}
        destructive={changing?.status === 'INACTIVE'}
        loading={setStatus.isPending}
        onConfirm={() => {
          if (changing !== null) {
            setStatus.mutate(changing);
          }
        }}
        onCancel={() => setChanging(null)}
      />
    </Card>
  );
}
